import type { FC, ReactNode } from 'react'
import type { Billing, CellValue, Plan } from '@/constants/pricing'

import Link from 'next/link'
import { useState } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTween } from '@/hooks/use-tween'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Accordion } from '@/components/generals/accordion'
import { CtaBand } from '@/components/generals/cta-band'
import { Landscape } from '@/components/generals/landscape'
import { PageHero } from '@/components/generals/page-hero'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'
import { COMPARISON, FAQS, PLANS } from '@/constants/pricing'
import { LANDSCAPES } from '@/lib/visual'

const Price = ({ plan, billing }: { plan: Plan, billing: Billing }) => {
    const target = billing === 'yearly' ? plan.yearly : plan.monthly
    const value = useTween(target, { from: target, duration: 700 })

    return (
        <p className='flex items-end gap-2'>
            <span className='text-6xl font-normal leading-none tracking-[-0.05em] tabular-nums text-cream'>
                <span aria-hidden='true'>
                    ${Math.round(value)}
                </span>

                <span className='sr-only'>
                    ${target}
                </span>
            </span>

            <span className='pb-1.5 text-sm leading-tight text-cream/55'>
                {plan.monthly === 0 ? 'for good' : 'a month'}
                {plan.monthly > 0 && billing === 'yearly' && (
                    <span className='block text-xs text-cream/40'>
                        ${plan.yearly * 12} paid once a year
                    </span>
                )}
            </span>
        </p>
    )
}

const Cell = ({ value }: { value: CellValue }) => {
    if (value === true) {
        return (
            <span className='inline-grid size-6 place-items-center rounded-full bg-citron/15 text-citron'>
                <Check aria-hidden='true' className='size-3.5' />
                <span className='sr-only'>Included</span>
            </span>
        )
    }

    if (value === false) {
        return (
            <span className='inline-grid size-6 place-items-center text-cream/25'>
                <Minus aria-hidden='true' className='size-4' />
                <span className='sr-only'>Not included</span>
            </span>
        )
    }

    return <span className='text-sm text-cream/80'>{value}</span>
}

const PricingPage: FC = (): ReactNode => {
    const [billing, setBilling] = useState<Billing>('yearly')

    return (
        <Layout>
            <Seo
                title='Pricing'
                description='Verdane pricing in plain terms: Free, Plus and Private. Stay on Free for good, try Plus for 14 days without a card, or add a certified advisor of your own.'
            />

            <PageHero
                eyebrow='Pricing'
                title='Honest prices, *nothing hidden*'
                subtitle='Begin on Free and stay for as long as it suits you. Move up for the complete AI advisor, or for a certified human advisor beside it.'
                image={LANDSCAPES.mistyRidges}
                position='center 70%'
            >
                <Reveal trigger='mount' delay={900} className='mt-10'>
                    <div role='radiogroup' aria-label='Billing period' className='glass relative flex rounded-full p-1'>
                        <span
                            aria-hidden='true'
                            className={cn(
                                'absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-full bg-cream transition-transform duration-500 ease-cinema',
                                billing === 'yearly' ? 'translate-x-full' : 'translate-x-0'
                            )}
                        />

                        {(['monthly', 'yearly'] as Array<Billing>).map(option => (
                            <button
                                key={option}
                                type='button'
                                role='radio'
                                aria-checked={billing === option}
                                onClick={() => setBilling(option)}
                                className={cn(
                                    'relative z-10 flex h-10 w-36 items-center justify-center gap-2 rounded-full text-sm capitalize transition-colors duration-500',
                                    billing === option ? 'text-black' : 'text-cream/70 hover:text-cream'
                                )}
                            >
                                {option}

                                {option === 'yearly' && (
                                    <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors duration-500', billing === 'yearly' ? 'bg-black text-citron' : 'bg-citron/15 text-citron')}>
                                        -20%
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </Reveal>
            </PageHero>

            <section aria-label='Plans' className='relative pb-28'>
                <div className='shell grid gap-5 lg:grid-cols-3 lg:items-stretch'>
                    {PLANS.map((plan, index) => (
                        <Reveal key={plan.id} delay={index * 140} className='h-full'>
                            <article
                                className={cn(
                                    'grain relative isolate flex h-full flex-col overflow-hidden rounded-[32px] p-7 sm:p-8',
                                    plan.highlighted ? 'bg-ink lg:-my-4 lg:py-12' : 'glass'
                                )}
                            >
                                {plan.highlighted && (
                                    <>
                                        <Landscape src={LANDSCAPES.pampas} fade='soft' className='-z-10' sizes='(min-width: 1024px) 33vw, 100vw' />
                                        <div aria-hidden='true' className='pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_0_0_1px_rgba(226,243,122,0.35),0_40px_100px_-40px_rgba(226,243,122,0.45)]' />
                                    </>
                                )}

                                <div className='flex items-center justify-between gap-3'>
                                    <h2 className='font-serif text-4xl italic text-cream'>
                                        {plan.name}
                                    </h2>

                                    {plan.highlighted && (
                                        <span className='rounded-full bg-citron px-3 py-1 text-xs font-medium text-black'>
                                            Most chosen
                                        </span>
                                    )}
                                </div>

                                <p className='mt-2 text-sm text-cream/60'>
                                    {plan.tagline}
                                </p>

                                <div className='mt-8'>
                                    <Price plan={plan} billing={billing} />
                                </div>

                                <ul className='mt-8 space-y-3.5 border-t border-white/10 pt-7'>
                                    {plan.features.map(feature => (
                                        <li key={feature} className='flex items-start gap-3 text-[15px] text-cream/85'>
                                            <span className='mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-citron/15 text-citron'>
                                                <Check aria-hidden='true' className='size-3' strokeWidth={3} />
                                            </span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <div className='mt-auto pt-10'>
                                    <PillButton href={`${plan.href}&billing=${billing}`} variant={plan.highlighted ? 'light' : 'glass'} className='w-full'>
                                        {plan.cta}
                                    </PillButton>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <p className='shell mt-10 text-center text-sm text-cream/45'>
                    No card to begin. Prices shown in US dollars. Leave whenever you like.
                </p>
            </section>

            <section aria-label='Compare plans' className='relative py-24'>
                <div className='shell'>
                    <SectionHeading
                        eyebrow='Compare'
                        title='Every detail, *in one view*'
                        subtitle='Pick the amount of help that suits where you are right now. Moving between plans stays open to you at every step.'
                    />

                    <Reveal className='mt-14'>
                        <div className='glass-dark overflow-hidden rounded-[30px]'>
                            <div className='overflow-x-auto'>
                                <table className='w-full min-w-[640px] border-collapse text-left'>
                                    <caption className='sr-only'>
                                        What each Verdane plan includes
                                    </caption>

                                    <thead>
                                        <tr className='border-b border-white/10'>
                                            <th scope='col' className='sticky left-0 bg-[#0b0c0a] px-6 py-5 text-xs font-medium uppercase tracking-[0.2em] text-cream/45 sm:bg-transparent'>
                                                What you get
                                            </th>

                                            {PLANS.map(plan => (
                                                <th key={plan.id} scope='col' className={cn('px-6 py-5 text-center font-serif text-2xl font-normal italic', plan.highlighted ? 'text-citron' : 'text-cream')}>
                                                    {plan.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>

                                    {COMPARISON.map(group => (
                                        <tbody key={group.group}>
                                            <tr>
                                                <th colSpan={4} scope='colgroup' className='px-6 pb-2 pt-8 text-[11px] font-medium uppercase tracking-[0.28em] text-cream/40'>
                                                    {group.group}
                                                </th>
                                            </tr>

                                            {group.rows.map(row => (
                                                <tr key={row.label} className='border-t border-white/[0.06]'>
                                                    <th scope='row' className='sticky left-0 bg-[#0b0c0a] px-6 py-4 text-sm font-normal text-cream/75 sm:bg-transparent'>
                                                        {row.label}
                                                    </th>

                                                    {row.values.map((value, index) => (
                                                        <td key={index} className={cn('px-6 py-4 text-center', PLANS[index].highlighted && 'bg-citron/[0.04]')}>
                                                            <Cell value={value} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    ))}
                                </table>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section aria-label='Frequently asked questions' className='relative py-24'>
                <div className='shell grid gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-20'>
                    <div>
                        <SectionHeading
                            eyebrow='FAQ'
                            title='Before you *choose*'
                            align='left'
                        />

                        <Reveal as='p' delay={300} className='mt-6 max-w-sm text-sm leading-relaxed text-cream/55'>
                            Still wondering about something? A person on our team answers within one business day. Just write to us on the{' '}
                            <Link href='/contact' className='text-cream underline decoration-cream/30 underline-offset-4 hover:decoration-citron'>
                                contact page
                            </Link>
                            .
                        </Reveal>
                    </div>

                    <Reveal>
                        <Accordion items={FAQS} />
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </Layout>
    )
}

export default PricingPage
